import { Link } from 'react-router-dom';
import { useLocalizedPath } from '@/hooks/useI18n';
import { useTranslation } from '@/hooks/useTranslation';
import { useState, useEffect } from 'react';
import { Container } from '@/components/Container';
import { getAllPosts, type BlogPost } from '@/content/blog/loader';
import { SEO } from '@/components/SEO';
import { OptimizedImage } from '@/components/OptimizedImage';

export function BlogListPage() {
  const localize = useLocalizedPath();
  const { t, lang } = useTranslation();
  const [posts, setPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    getAllPosts(lang).then(setPosts);
  }, [lang]);

  return (
    <div>
      <SEO
        title={t('blog.seoTitle')}
        description={t('blog.seoDescription')}
        keywords={t('blog.seoKeywords')}
        canonical="/blog"
      />
      {/* Hero */}
      <section className="relative h-[50vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <OptimizedImage
            src="/images/praia-do-farol-hero.jpg"
            alt={t('blog.heroImageAlt')}
            className="absolute inset-0 w-full h-full object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />
        </div>
        <Container className="relative z-10 pb-12 pt-32">
          <div className="max-w-3xl">
            <span className="font-body text-sm text-coral font-semibold uppercase tracking-wider">
              {t('blog.heroBadge')}
            </span>
            <h1 className="font-display text-5xl sm:text-6xl text-white mt-2 mb-4">
              {t('blog.heroTitle')}
            </h1>
            <p className="font-editorial text-xl text-white/90">{t('blog.heroSubtitle')}</p>
          </div>
        </Container>
      </section>

      {/* Lista de posts */}
      <section className="py-16 sm:py-20 bg-sand-50">
        <Container className="max-w-4xl">
          <div className="space-y-8">
            {posts.map(post => (
              <article
                key={post.slug}
                className="bg-white rounded-2xl border border-sand-200 overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-md transition-shadow"
              >
                <div className="grid md:grid-cols-3 gap-0">
                  <div className="h-48 md:h-auto overflow-hidden">
                    <img
                      src={post.coverImage}
                      alt={post.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="md:col-span-2 p-6 flex flex-col justify-center">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {post.tags.map(tag => (
                        <span
                          key={tag}
                          className="font-body text-[10px] font-bold uppercase tracking-wider bg-teal-light/10 text-teal-deep border border-teal-light/20 px-2 py-0.5 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h2 className="font-display text-2xl text-warm-dark mb-2 hover:text-teal-deep transition-colors">
                      <Link to={localize(`/blog/${post.slug}`)}>{post.title.toUpperCase()}</Link>
                    </h2>
                    <p className="font-body text-warm-gray leading-relaxed mb-4">{post.excerpt}</p>
                    <div className="flex items-center gap-4 font-body text-xs text-warm-gray">
                      <span>{post.author}</span>
                      <span>•</span>
                      <span>{new Date(post.publishedAt).toLocaleDateString(lang)}</span>
                      <span>•</span>
                      <span>{t('blog.readingTime').replace('{{time}}', post.readingTime)}</span>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </Container>
      </section>
    </div>
  );
}
